import { useState } from "react";
import { Capacitor } from "@capacitor/core";

import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import { useDeviceId } from "@/lib/device/DeviceProvider";
import { requestNotificationPermission } from "@/lib/native/notifications";
import { persistNotificationPermissionToConvex } from "@/lib/native/notificationPermissionSync";
import { useConvex } from "convex/react";

import { EmphasisText } from "../EmphasisText";

import { NotificationsIllustration } from "./NotificationsIllustration";
import { useOptionalOnboardingAnalytics } from "@/lib/onboarding/OnboardingAnalyticsContext";
import { useOptionalTabPaywallAnalytics } from "@/lib/monetization/TabPaywallAnalyticsContext";

export function NotificationsSlide({ denied = false }: { denied?: boolean }) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);

  return (
    <div className="flex flex-1 flex-col items-center gap-8 pb-4 pt-2">
      <h1 className="max-w-md text-center text-[1.35rem] font-semibold leading-snug text-[oklch(92%_0.02_150)] sm:text-2xl">
        <EmphasisText text={t.onboarding.notificationsHeadline} />
      </h1>

      <NotificationsIllustration />

      {denied ? (
        <p className="max-w-sm text-center text-base font-medium leading-7 text-[oklch(80%_0.12_55)]">
          {t.onboarding.notificationsDeniedWarning}
        </p>
      ) : (
        <p className="max-w-sm text-center text-base leading-7 text-[oklch(82%_0.025_155)]">
          {t.onboarding.notificationsBody}
        </p>
      )}
    </div>
  );
}

export function useNotificationsSlideAction(onAdvance: () => void, options?: { step?: number }) {
  const analyticsStep = options?.step ?? 2;
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const isNative = Capacitor.isNativePlatform();
  const deviceId = useDeviceId();
  const convex = useConvex();
  const analytics = useOptionalTabPaywallAnalytics() ?? useOptionalOnboardingAnalytics();
  const [notificationsBusy, setNotificationsBusy] = useState(false);
  const [notificationsDenied, setNotificationsDenied] = useState(false);

  async function handleAllowCta() {
    if (notificationsBusy) return;

    analytics?.trackCtaClick(analyticsStep, "primary");

    // Web has no native push permission to request — advance gracefully.
    if (!isNative) {
      analytics?.trackPermissionOutcome(analyticsStep, "notifications", "skipped_web");
      onAdvance();
      return;
    }

    setNotificationsBusy(true);
    try {
      const result = await requestNotificationPermission(deviceId);
      await persistNotificationPermissionToConvex(convex, deviceId, result);
      analytics?.trackPermissionOutcome(analyticsStep, "notifications", result);
      if (result === "granted" || result === "unsupported") {
        onAdvance();
      } else {
        // "denied" (just declined) or "blocked" (Settings opened): stay and warn.
        setNotificationsDenied(true);
      }
    } catch {
      analytics?.trackPermissionOutcome(analyticsStep, "notifications", "error");
      // Unexpected SDK failure shouldn't trap the user on this step.
      onAdvance();
    } finally {
      setNotificationsBusy(false);
    }
  }

  function handleMaybeLater() {
    analytics?.trackPermissionOutcome(analyticsStep, "notifications", "skipped");
    onAdvance();
  }

  return {
    handleAllowCta,
    handleMaybeLater,
    ctaLabel: t.onboarding.notificationsAllow,
    secondaryCtaLabel: t.onboarding.notificationsMaybeLater,
    notificationsBusy,
    notificationsDenied,
  };
}
